import { sepaValue } from "./sepa";
import type { AddressValue, DateRangeValue, Field, FieldOf } from "@fobu/schema";

/* ---------------- Adresse ---------------- */

/** Postleitzahl-Format je Land (grob – Ziel: Tippfehler abfangen, nicht jede Sonderform). */
const ZIP: Record<string, RegExp> = {
  DE: /^\d{5}$/,
  AT: /^\d{4}$/,
  CH: /^\d{4}$/,
  LI: /^\d{4}$/,
  LU: /^(L-?)?\d{4}$/i,
  NL: /^\d{4}\s?[A-Za-z]{2}$/,
  BE: /^\d{4}$/,
  FR: /^\d{5}$/,
  IT: /^\d{5}$/,
  PL: /^\d{2}-\d{3}$/,
  CZ: /^\d{3}\s?\d{2}$/,
  DK: /^\d{4}$/,
};
export const COUNTRY_NAMES: Record<string, { de: string; en: string }> = {
  DE: { de: "Deutschland", en: "Germany" },
  AT: { de: "Österreich", en: "Austria" },
  CH: { de: "Schweiz", en: "Switzerland" },
  LI: { de: "Liechtenstein", en: "Liechtenstein" },
  LU: { de: "Luxemburg", en: "Luxembourg" },
  NL: { de: "Niederlande", en: "Netherlands" },
  BE: { de: "Belgien", en: "Belgium" },
  FR: { de: "Frankreich", en: "France" },
  IT: { de: "Italien", en: "Italy" },
  PL: { de: "Polen", en: "Poland" },
  CZ: { de: "Tschechien", en: "Czechia" },
  DK: { de: "Dänemark", en: "Denmark" },
};

const str = (v: unknown) => (typeof v === "string" ? v.trim() : "");

/** Antwort lesen (getrimmt); leere Teile entfallen. */
export function addressValue(value: unknown): AddressValue | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  const out: AddressValue = { street: str(v.street), zip: str(v.zip), city: str(v.city) };
  if (str(v.line2)) out.line2 = str(v.line2);
  if (str(v.country)) out.country = str(v.country).toUpperCase();
  return out;
}

export const addressCountry = (field: FieldOf<"address">, v: AddressValue | null) => (field.country !== "hidden" && v?.country) || field.defaultCountry;

export function validateAddress(field: FieldOf<"address">, value: unknown): { code: "invalid_type" | "incomplete_address" | "invalid_zip" | "too_long"; message: string } | null {
  const v = addressValue(value);
  if (!v) return { code: "invalid_type", message: "Ungültige Adresse." };
  if ([v.street, v.zip, v.city, v.line2 ?? "", v.country ?? ""].some((x) => x.length > 200)) return { code: "too_long", message: "Eine Angabe ist zu lang." };
  const missing = [!v.street && "Straße und Hausnummer", !v.zip && "PLZ", !v.city && "Ort"].filter(Boolean);
  if (missing.length) return { code: "incomplete_address", message: `Bitte noch angeben: ${missing.join(", ")}.` };
  const country = addressCountry(field, v);
  if (country && !(country in COUNTRY_NAMES)) return { code: "invalid_type", message: "Unbekanntes Land." };
  const re = ZIP[country];
  if (re && !re.test(v.zip)) return { code: "invalid_zip", message: `Die Postleitzahl passt nicht zu ${COUNTRY_NAMES[country]?.de ?? country}.` };
  return null;
}

/** „Musterstraße 1, c/o Schmidt, 69412 Eberbach“ – Land nur, wenn abweichend vom Standard. */
export function formatAddress(field: FieldOf<"address">, value: unknown, lang = "de"): string {
  const v = addressValue(value);
  if (!v) return "";
  const country = addressCountry(field, v);
  const showCountry = country !== field.defaultCountry || field.country === "required";
  const name = COUNTRY_NAMES[country]?.[lang.startsWith("de") ? "de" : "en"] ?? country;
  return [v.street, v.line2, [v.zip, v.city].filter(Boolean).join(" "), showCountry ? name : ""].filter(Boolean).join(", ");
}

/* ---------------- Zeitraum ---------------- */

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const dayNo = (d: string) => Date.UTC(Number(d.slice(0, 4)), Number(d.slice(5, 7)) - 1, Number(d.slice(8, 10))) / 864e5;
const validDay = (d: string) => DATE.test(d) && new Date(`${d}T00:00:00Z`).toISOString().slice(0, 10) === d;

export function dateRangeValue(value: unknown): DateRangeValue | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  return { from: str(v.from), to: str(v.to) };
}

/** Tage inklusive Start- und Endtag. */
export const rangeDays = (r: DateRangeValue) => dayNo(r.to) - dayNo(r.from) + 1;

export function validateDateRange(field: FieldOf<"date_range">, value: unknown): { code: "invalid_type" | "invalid_date" | "invalid_range" | "date_too_early" | "date_too_late" | "range_too_long" | "range_too_short"; message: string } | null {
  const r = dateRangeValue(value);
  if (!r) return { code: "invalid_type", message: "Ungültiger Zeitraum." };
  if (!r.from || !r.to) return { code: "invalid_date", message: !r.from ? "Bitte das Startdatum angeben." : "Bitte das Enddatum angeben." };
  if (!validDay(r.from) || !validDay(r.to)) return { code: "invalid_date", message: "Bitte gültige Daten eingeben." };
  if (r.to < r.from) return { code: "invalid_range", message: "Das Enddatum liegt vor dem Startdatum." };
  const de = (d: string) => d.split("-").reverse().join(".");
  if (field.minDate && r.from < field.minDate) return { code: "date_too_early", message: `Der Zeitraum darf nicht vor dem ${de(field.minDate)} beginnen.` };
  if (field.maxDate && r.to > field.maxDate) return { code: "date_too_late", message: `Der Zeitraum darf nicht nach dem ${de(field.maxDate)} enden.` };
  const days = rangeDays(r);
  if (field.maxDays && days > field.maxDays) return { code: "range_too_long", message: `Höchstens ${field.maxDays} Tage.` };
  if (field.minDays && days < field.minDays) return { code: "range_too_short", message: `Mindestens ${field.minDays} Tage.` };
  return null;
}

export function formatDateRange(value: unknown, lang = "de"): string {
  const r = dateRangeValue(value);
  if (!r || !r.from) return "";
  const de = lang.startsWith("de");
  const f = (d: string) => (de ? d.split("-").reverse().join(".") : d);
  if (!r.to || r.to === r.from) return f(r.from);
  const n = validDay(r.from) && validDay(r.to) ? rangeDays(r) : 0;
  return `${f(r.from)} – ${f(r.to)}${n > 1 ? (de ? ` (${n} Tage)` : ` (${n} days)`) : ""}`;
}

/** Leere Teile entfernen, ganz leere Antworten zu `undefined`. */
export function cleanComposite(field: Field, value: unknown): unknown {
  if (field.type === "sepa") {
    const v = sepaValue(value);
    if (!v || (!v.holder && !v.iban && !v.bic && !v.accepted)) return undefined;
    // Referenz und Datum vergibt der Server – nie vom Browser übernehmen.
    delete v.reference;
    delete v.date;
    if (!field.askBic && !v.bic) delete v.bic;
    return v;
  }
  if (field.type === "address") {
    const v = addressValue(value);
    if (!v || (!v.street && !v.zip && !v.city && !v.line2 && (!v.country || field.country === "hidden"))) return undefined;
    if (field.country === "hidden") delete v.country;
    // Auswahl unverändert gelassen = voreingestelltes Land (so zeigt es das Formular auch an)
    else if (!v.country) v.country = field.defaultCountry;
    if (!field.line2) delete v.line2;
    return v;
  }
  if (field.type === "date_range") {
    const r = dateRangeValue(value);
    return r && (r.from || r.to) ? r : undefined;
  }
  return value;
}
